import { useCallback, useEffect, useRef, useState } from "react";
import { PhoneIncoming, PhoneOff, Volume2, X, MapPin } from "lucide-react";
import type {
  CallQuestion,
  EmergencyCall,
  Point,
  SimState,
} from "../domain/model";
import {
  incidentProfiles,
  profileIds,
  incidentCode,
  type ProfileId,
} from "../domain/incidentProfiles";
import { defaultCallSettings, questionLabels } from "../domain/emergencyCalls";
import { DirectSpeech, type DirectState } from "../audio/directSpeech";
import type { RadioController } from "../audio/useRadioAudio";
import type { Perform } from "./ResourceManager";
export function CallTimingSettings({
  state,
  act,
  enabled,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
}) {
  const settings = state.callSettings ?? defaultCallSettings;
  const [min, setMin] = useState(settings.minSeconds),
    [max, setMax] = useState(settings.maxSeconds),
    [limit, setLimit] = useState(settings.maxWaiting),
    [scale, setScale] = useState(settings.scaleWithFleet);
  const [saved, setSaved] = useState(false);
  return (
    <section className="call-timing">
      <h3>Notrufaufkommen</h3>
      <p className="muted small">
        Zufällige Intervalle in Simulationssekunden. 30–3.600 Sekunden; Pause
        und Zeitbeschleunigung gelten auch für Anrufe.
      </p>
      <div className="call-timing-fields">
        <label>
          Frühestens nach
          <input
            aria-label="Minimales Notrufintervall"
            type="number"
            min="30"
            max="3600"
            value={min}
            onChange={(e) => {
              setMin(Number(e.target.value));
              setSaved(false);
            }}
          />
        </label>
        <label>
          Spätestens nach
          <input
            aria-label="Maximales Notrufintervall"
            type="number"
            min="30"
            max="3600"
            value={max}
            onChange={(e) => {
              setMax(Number(e.target.value));
              setSaved(false);
            }}
          />
        </label>
        <label>
          Wartende Anrufe
          <input
            aria-label="Maximale wartende Anrufe"
            type="number"
            min="1"
            max="10"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setSaved(false);
            }}
          />
        </label>
      </div>
      <label className="switch-row">
        <span>
          An Flottengröße anpassen
          <small>
            Referenz 20 aktive Fahrzeuge; größere Flotte = kürzere Intervalle,
            höchstens Faktor 3.
          </small>
        </span>
        <input
          type="checkbox"
          checked={scale}
          onChange={(e) => {
            setScale(e.target.checked);
            setSaved(false);
          }}
        />
      </label>
      <button
        className="button secondary"
        disabled={
          !enabled ||
          min < 30 ||
          max < min ||
          max > 3600 ||
          limit < 1 ||
          limit > 10
        }
        onClick={async () => {
          if (
            await act(
              {
                type: "settings",
                callSettings: {
                  minSeconds: min,
                  maxSeconds: max,
                  maxWaiting: limit,
                  scaleWithFleet: scale,
                },
              },
              "Notrufintervalle gespeichert.",
            )
          )
            setSaved(true);
        }}
      >
        {saved ? "Gespeichert" : "Notrufintervalle speichern"}
      </button>
    </section>
  );
}
export default function CallDesk({
  state,
  act,
  enabled,
  audio,
  onIncident,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
  audio: RadioController;
  onIncident: (id: string, point: Point) => void;
}) {
  const [open, setOpen] = useState(false),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [profile, setProfile] = useState<ProfileId | "">(""),
    [exercise, setExercise] = useState<ProfileId | "">(""),
    [report, setReport] = useState(
      "Anrufer meldet einen Notfall. Weitere Abfrage läuft.",
    );
  const [voice, setVoice] = useState<Partial<DirectState>>({});
  const speech = useRef<DirectSpeech | null>(null),
    element = useRef<HTMLAudioElement | null>(null),
    abort = useRef<AbortController | null>(null),
    sequence = useRef(0);
  const active = state.calls?.find((c) => c.phase === "active");
  const waiting = state.calls?.filter((c) => c.phase === "ringing") ?? [];
  const currentCall = useRef(active?.id);
  currentCall.current = active?.id;
  useEffect(() => {
    const engine = new DirectSpeech(setVoice, audio.setCallerSpeaking);
    speech.current = engine;
    engine.attach(element.current);
    return () => {
      sequence.current++;
      abort.current?.abort();
      engine.dispose();
      speech.current = null;
    };
  }, [audio.setCallerSpeaking]);
  const audioRef = useCallback((el: HTMLAudioElement | null) => {
    element.current = el;
    speech.current?.attach(el);
  }, []);
  useEffect(() => {
    setProfile("");
    setReport("Anrufer meldet einen Notfall. Weitere Abfrage läuft.");
    setError("");
    sequence.current++;
    abort.current?.abort();
    speech.current?.stop();
    setLoading(false);
  }, [active?.id]);
  async function play(call: EmergencyCall, question: CallQuestion) {
    const token = ++sequence.current;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/caller-speech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ callId: call.id, question }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const body = await res.json();
        throw Error(body.error ?? "Anruferstimme nicht verfügbar.");
      }
      const blob = await res.blob();
      if (token !== sequence.current) return;
      await speech.current?.play(blob, 0.85);
    } catch (e) {
      if (token === sequence.current && !controller.signal.aborted)
        setError(
          (e as Error).message + " Die Antwort steht im Gesprächsprotokoll.",
        );
    } finally {
      if (token === sequence.current) setLoading(false);
    }
  }
  async function accept(call: EmergencyCall) {
    void speech.current?.unlock(0.85);
    setOpen(true);
    const result = await act({ type: "acceptCall", callId: call.id });
    // Playback starts on the next UI turn, after the active-call reset effect.
    if (result)
      setTimeout(() => {
        if (currentCall.current === call.id)
          void play(result.calls!.find((c) => c.id === call.id)!, "opening");
      }, 100);
  }
  async function ask(question: CallQuestion) {
    if (!active || loading) return;
    void speech.current?.unlock(0.85);
    const result = await act({ type: "askCall", callId: active.id, question });
    if (result) {
      const call = result.calls!.find((c) => c.id === active.id)!;
      if (question === "what") {
        setReport(call.answers.what!);
        if (!profile) setProfile(call.profileId);
      }
      void play(call, question);
    }
  }
  async function end() {
    if (!active) return;
    const result = await act(
      { type: "endCall", callId: active.id },
      "Gespräch beendet.",
    );
    if (result) {
      sequence.current++;
      abort.current?.abort();
      speech.current?.stop();
      setLoading(false);
    }
  }
  return (
    <aside
      className={"call-desk " + (open ? "expanded" : "collapsed")}
      aria-label="Notrufannahme"
    >
      <button
        className={"call-desk-toggle " + (waiting.length ? "ringing" : "")}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <PhoneIncoming size={19} />
        <b>{active ? "Notruf im Gespräch" : "Notrufannahme"}</b>
        <span>{waiting.length} warten</span>
        {open ? <X size={17} /> : null}
      </button>
      <div className="call-desk-body" hidden={!open}>
        <div className="call-desk-meta">
          <span className="live-dot" />
          {state.autoGenerate
            ? "Automatischer Eingang aktiv"
            : "Automatischer Eingang pausiert"}{" "}
          · {state.running ? "Simulation läuft" : "Zeit angehalten"}
        </div>
        {waiting.length > 0 && (
          <div className="call-waiting" aria-label="Wartende Notrufe">
            {waiting.map((c, n) => (
              <button
                key={c.id}
                className="button secondary"
                disabled={!enabled || !!active}
                onClick={() => void accept(c)}
              >
                <PhoneIncoming size={16} />
                <span>
                  Notruf {n + 1} ·{" "}
                  {Math.max(0, Math.floor((state.now - c.createdAt) / 1000))} s
                  wartet
                </span>
                <b>Annehmen</b>
              </button>
            ))}
          </div>
        )}
        {!active && (
          <>
            <p className="muted">
              Eingehende Notrufe annehmen, den Anrufer abfragen und parallel
              einen Einsatz disponieren.
            </p>
            <label>
              Übungsszenario
              <select
                aria-label="Übungsszenario"
                value={exercise}
                onChange={(e) => setExercise(e.target.value as ProfileId | "")}
              >
                <option value="">Zufälliges Szenario</option>
                {profileIds.map((id) => (
                  <option key={id} value={id}>
                    {incidentCode({
                      type: incidentProfiles[id].type,
                      profileId: id,
                    })}{" "}
                    · {incidentProfiles[id].title}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="button secondary"
              disabled={!enabled}
              onClick={() =>
                void act(
                  { type: "generateCall", profileId: exercise || undefined },
                  "Notruf geht ein.",
                )
              }
            >
              <PhoneIncoming size={16} />
              Übungsanruf auslösen
            </button>
          </>
        )}
        {active && (
          <>
            <div className="call-location">
              <MapPin size={17} />
              <span>
                <b>{active.address}</b>
                <small>
                  {active.district} · Standort automatisch verknüpft
                </small>
              </span>
            </div>
            <div className="call-questions" aria-label="Notrufabfrage">
              {(
                [
                  "where",
                  "who",
                  "what",
                  "how",
                  "count",
                  "danger",
                ] as CallQuestion[]
              ).map((q) => (
                <button
                  className={active.answered.includes(q) ? "answered" : ""}
                  key={q}
                  disabled={!enabled || loading}
                  onClick={() => void ask(q)}
                >
                  {active.answered.includes(q) ? "✓ " : ""}
                  {questionLabels[q]}
                </button>
              ))}
            </div>
            <div className="call-transcript" aria-label="Gesprächsprotokoll">
              {active.answered.map((q) => (
                <div key={q}>
                  <strong>{questionLabels[q]}</strong>
                  <p>{active.answers[q]}</p>
                  <button
                    className="text-button"
                    disabled={loading}
                    onClick={() => {
                      void speech.current?.unlock(0.85);
                      void play(active, q);
                    }}
                  >
                    <Volume2 size={13} />
                    Antwort wiederholen
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="caller-player">
          <small>
            {loading
              ? "Anruferstimme · " +
                (voice.directPhase === "playing" ? "spricht" : "wird geladen …")
              : "Telefonstimme · separates deutsches Sprachmodell"}
          </small>
          <audio
            ref={audioRef}
            controls
            preload="auto"
            aria-label="Stimme des Anrufers"
          />
          {loading && (
            <button
              className="text-button"
              onClick={() => {
                sequence.current++;
                abort.current?.abort();
                speech.current?.stop();
                setLoading(false);
              }}
            >
              Antwort stoppen
            </button>
          )}
          {voice.directError && <p role="status">{voice.directError}</p>}
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
        </div>
        {active && !active.incidentId && (
          <form
            className="call-disposition"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!profile) return;
              const result = await act(
                {
                  type: "incidentFromCall",
                  callId: active.id,
                  profileId: profile,
                  report,
                },
                "Einsatz aus Notruf erstellt.",
              );
              if (result) {
                const call = result.calls!.find((c) => c.id === active.id)!;
                const i = result.incidents.find(
                  (i) => i.id === call.incidentId,
                )!;
                onIncident(i.id, i.location);
              }
            }}
          >
            <h3>Einsatz parallel anlegen</h3>
            <label>
              Einsatzstichwort
              <select
                required
                aria-label="Einsatzstichwort aus Notruf"
                value={profile}
                onChange={(e) => setProfile(e.target.value as ProfileId)}
              >
                <option value="">Stichwort festlegen …</option>
                {profileIds.map((id) => (
                  <option key={id} value={id}>
                    {incidentCode({
                      type: incidentProfiles[id].type,
                      profileId: id,
                    })}{" "}
                    · {incidentProfiles[id].title}
                  </option>
                ))}
              </select>
            </label>
            {profile && (
              <p className="muted small">
                Planungsbedarf:{" "}
                {Object.entries(incidentProfiles[profile].needs)
                  .map(([kind, n]) => n + " " + kind)
                  .join(", ")}
                . Tatsächliche Lage kann abweichen.
              </p>
            )}
            <label>
              Meldungsbild
              <textarea
                aria-label="Meldungsbild aus Notruf"
                rows={3}
                minLength={5}
                maxLength={1500}
                value={report}
                onChange={(e) => setReport(e.target.value)}
              />
            </label>
            <button className="button primary" disabled={!enabled || !profile}>
              Einsatz erstellen · Adresse übernehmen
            </button>
          </form>
        )}
        {active?.incidentId && (
          <div className="call-linked">
            <button
              className="button primary"
              onClick={() => {
                onIncident(active.incidentId!, active.location);
                setOpen(false);
              }}
            >
              Verknüpften Einsatz disponieren
            </button>
            <button
              className="button secondary"
              disabled={!enabled}
              onClick={() => void end()}
            >
              <PhoneOff size={16} />
              Gespräch beenden
            </button>
            <small>
              Du kannst weiter abfragen und gleichzeitig Kräfte alarmieren. Funk
              wartet nur während einer gesprochenen Antwort und läuft danach
              automatisch weiter.
            </small>
          </div>
        )}
      </div>
    </aside>
  );
}
